<script setup>
import { computed, onMounted, inject, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import KnowledgeBaseSelectorDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/KnowledgeBaseSelectorDialog.vue';
import HelpCenterSelectorDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/HelpCenterSelectorDialog.vue';

// 导入图片
import documentPopoverLight from 'assets/images/dashboard/pilot/document-popover-light.svg';
import documentPopoverDark from 'assets/images/dashboard/pilot/document-popover-dark.svg';
import faqsPopoverLight from 'assets/images/dashboard/pilot/faqs-popover-light.svg';
import faqsPopoverDark from 'assets/images/dashboard/pilot/faqs-popover-dark.svg';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();

const webSearchState = ref({
  enabled: false,
});
const isSavingWebSearch = ref(false);

const assistantConfig = computed(() => props.assistant?.config || {});

watch(
  () => props.assistant?.config?.web_search,
  config => {
    webSearchState.value = {
      enabled: config?.enabled === true,
    };
  },
  { immediate: true, deep: true }
);

// 检查帮助中心功能是否开启
const getAccount = useMapGetter('accounts/getAccount');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentAccount = computed(() => getAccount.value(currentAccountId.value));
const isHelpCenterEnabled = computed(() => {
  const features = currentAccount.value?.features;
  if (!features || Object.keys(features).length === 0) return true;
  return features.help_center === true;
});

// 从父组件注入 assistantId
const injectedAssistantId = inject('assistantId');
const assistantId = computed(
  () => injectedAssistantId?.value || props.assistant?.id
);

// Store getters
const assistantKnowledgeBases = useMapGetter(
  'pilotAssistantKnowledgeBases/getRecords'
);
const assistantPortals = useMapGetter('pilotAssistantPortals/getRecords');

// 已关联的知识库（直接使用 assistantKnowledgeBases）
const linkedKnowledgeBases = computed(() => {
  return assistantKnowledgeBases.value || [];
});

// 搜索关键词
const searchQuery = ref('');

// 根据搜索过滤的已关联知识库
const filteredLinkedKnowledgeBases = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return linkedKnowledgeBases.value;

  return linkedKnowledgeBases.value.filter(kb => {
    const name = (kb.name || '').toLowerCase();
    const description = (kb.description || '').toLowerCase();
    return name.includes(query) || description.includes(query);
  });
});

// 弹窗引用
const knowledgeBaseSelectorRef = ref(null);
const isAddingKnowledgeBases = ref(false);

// 批量选择状态
const selectedKnowledgeBaseIds = ref(new Set());
const isBatchRemoving = ref(false);

// hover 状态
const hoveredKnowledgeBaseId = ref(null);
const hoveredPortalId = ref(null);

// 打开知识库选择弹窗
const openKnowledgeBaseSelector = () => {
  knowledgeBaseSelectorRef.value?.open();
};

// 处理知识库选择确认
const handleKnowledgeBaseConfirm = async knowledgeBaseIds => {
  if (!knowledgeBaseIds?.length) return;

  isAddingKnowledgeBases.value = true;
  try {
    // 批量添加知识库关联
    await Promise.all(
      knowledgeBaseIds.map(kbId =>
        store.dispatch('pilotAssistantKnowledgeBases/create', {
          assistantId: assistantId.value,
          knowledgeBaseId: kbId,
        })
      )
    );
    // 重新获取关联列表
    await store.dispatch('pilotAssistantKnowledgeBases/get', {
      assistantId: assistantId.value,
    });
  } finally {
    isAddingKnowledgeBases.value = false;
  }
};

const buildWebSearchConfig = () => ({
  enabled: webSearchState.value.enabled,
  search_options: {
    search_strategy: 'turbo',
    enable_source: true,
  },
});

const saveWebSearchConfig = async () => {
  if (!props.assistant?.id || isSavingWebSearch.value) return;

  isSavingWebSearch.value = true;
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        config: {
          ...assistantConfig.value,
          web_search: buildWebSearchConfig(),
        },
      },
    });
    useAlert('联网搜索配置已保存');
  } catch (error) {
    useAlert(error?.message || '联网搜索配置保存失败');
  } finally {
    isSavingWebSearch.value = false;
  }
};

const handleToggleWebSearch = value => {
  webSearchState.value.enabled = value;
  saveWebSearchConfig();
};

// 切换选中状态
const toggleSelection = kbId => {
  if (selectedKnowledgeBaseIds.value.has(kbId)) {
    selectedKnowledgeBaseIds.value.delete(kbId);
  } else {
    selectedKnowledgeBaseIds.value.add(kbId);
  }
  selectedKnowledgeBaseIds.value = new Set(selectedKnowledgeBaseIds.value);
};

// 判断是否选中
const isSelected = kbId => selectedKnowledgeBaseIds.value.has(kbId);

// 选中数量
const selectedCount = computed(() => selectedKnowledgeBaseIds.value.size);
const hasSelection = computed(() => selectedKnowledgeBaseIds.value.size > 0);

// 全选/取消全选标签
const buildSelectedCountLabel = computed(() => {
  const count = linkedKnowledgeBases.value.length || 0;
  const isAllSelected =
    selectedKnowledgeBaseIds.value.size === count && count > 0;
  return isAllSelected ? `取消全选 (${count})` : `全选 (${count})`;
});

const selectedCountLabel = computed(() => {
  return `已选择 ${selectedKnowledgeBaseIds.value.size} 项`;
});

// 加载数据
const fetchData = async () => {
  if (!assistantId.value) return;
  const tasks = [
    store.dispatch('pilotAssistantKnowledgeBases/get', {
      assistantId: assistantId.value,
    }),
  ];
  if (isHelpCenterEnabled.value) {
    tasks.push(
      store.dispatch('pilotAssistantPortals/get', {
        assistantId: assistantId.value,
      })
    );
  }
  await Promise.all(tasks);
};

// 获取知识库类型标签
const getTypeLabel = kb => {
  if (kb.knowledge_base_type === 'faq') {
    return 'FAQs';
  }
  return '文档';
};

// 获取知识库类型颜色类
const getTypeColorClass = kb => {
  if (kb.knowledge_base_type === 'faq') {
    return 'bg-n-emerald-100 text-n-emerald-700 dark:bg-n-emerald-900/30 dark:text-n-emerald-400';
  }
  return 'bg-n-blue-100 text-n-blue-700 dark:bg-n-blue-900/30 dark:text-n-blue-400';
};

// 获取文档数量
const getDocumentsCount = kb => {
  return kb.documents_count || 0;
};

// 获取FAQ数量
const getFaqsCount = kb => {
  return kb.faqs_count || 0;
};

// 移除知识库
const removingId = ref(null);
const handleRemoveKnowledgeBase = async kb => {
  removingId.value = kb.id;
  try {
    await store.dispatch('pilotAssistantKnowledgeBases/delete', {
      assistantId: assistantId.value,
      id: kb.id,
    });
    await store.dispatch('pilotAssistantKnowledgeBases/get', {
      assistantId: assistantId.value,
    });
  } finally {
    removingId.value = null;
  }
};

// 批量移除
const handleBatchRemove = async () => {
  if (!hasSelection.value || isBatchRemoving.value) return;

  isBatchRemoving.value = true;
  try {
    const ids = Array.from(selectedKnowledgeBaseIds.value);
    await Promise.all(
      ids.map(kbId =>
        store.dispatch('pilotAssistantKnowledgeBases/delete', {
          assistantId: assistantId.value,
          id: kbId,
        })
      )
    );
    await store.dispatch('pilotAssistantKnowledgeBases/get', {
      assistantId: assistantId.value,
    });
    selectedKnowledgeBaseIds.value = new Set();
  } finally {
    isBatchRemoving.value = false;
  }
};

// 已关联的帮助中心（直接使用 assistantPortals）
const linkedPortals = computed(() => {
  return assistantPortals.value || [];
});

// 帮助中心搜索
const portalSearchQuery = ref('');

// 根据搜索过滤的已关联帮助中心
const filteredLinkedPortals = computed(() => {
  const query = portalSearchQuery.value.toLowerCase().trim();
  if (!query) return linkedPortals.value;

  return linkedPortals.value.filter(portal => {
    const name = (portal.name || '').toLowerCase();
    const headerText = (portal.header_text || '').toLowerCase();
    return name.includes(query) || headerText.includes(query);
  });
});

// 帮助中心弹窗引用
const helpCenterSelectorRef = ref(null);

// 打开帮助中心选择弹窗
const openHelpCenterSelector = () => {
  helpCenterSelectorRef.value?.open();
};

// 移除帮助中心
const removingPortalId = ref(null);
const handleRemovePortal = async portal => {
  removingPortalId.value = portal.id;
  try {
    await store.dispatch('pilotAssistantPortals/delete', {
      assistantId: assistantId.value,
      id: portal.id,
    });
    await store.dispatch('pilotAssistantPortals/get', {
      assistantId: assistantId.value,
    });
  } finally {
    removingPortalId.value = null;
  }
};

// 帮助中心批量选择状态
const selectedPortalIds = ref(new Set());
const isBatchRemovingPortals = ref(false);

// 切换帮助中心选中状态
const togglePortalSelection = portalId => {
  if (selectedPortalIds.value.has(portalId)) {
    selectedPortalIds.value.delete(portalId);
  } else {
    selectedPortalIds.value.add(portalId);
  }
  selectedPortalIds.value = new Set(selectedPortalIds.value);
};

// 判断帮助中心是否选中
const isPortalSelected = portalId => selectedPortalIds.value.has(portalId);

// 帮助中心选中数量
const selectedPortalCount = computed(() => selectedPortalIds.value.size);
const hasPortalSelection = computed(() => selectedPortalIds.value.size > 0);

// 帮助中心全选/取消全选标签
const buildPortalSelectedCountLabel = computed(() => {
  const count = linkedPortals.value.length || 0;
  const isAllSelected = selectedPortalIds.value.size === count && count > 0;
  return isAllSelected ? `取消全选 (${count})` : `全选 (${count})`;
});

const selectedPortalCountLabel = computed(() => {
  return `已选择 ${selectedPortalIds.value.size} 项`;
});

// 批量移除帮助中心
const handleBatchRemovePortals = async () => {
  if (!hasPortalSelection.value || isBatchRemovingPortals.value) return;

  isBatchRemovingPortals.value = true;
  try {
    const ids = Array.from(selectedPortalIds.value);
    await Promise.all(
      ids.map(portalId =>
        store.dispatch('pilotAssistantPortals/delete', {
          assistantId: assistantId.value,
          id: portalId,
        })
      )
    );
    await store.dispatch('pilotAssistantPortals/get', {
      assistantId: assistantId.value,
    });
    selectedPortalIds.value = new Set();
  } finally {
    isBatchRemovingPortals.value = false;
  }
};

onMounted(() => {
  fetchData();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- 一级标题 -->
    <h2 class="text-base font-semibold text-n-slate-12 pt-4">知识</h2>

    <!-- 联网搜索 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-start gap-3">
          <div
            class="size-10 rounded-xl bg-n-blue-100 dark:bg-n-blue-900/30 flex items-center justify-center shrink-0"
          >
            <span class="i-lucide-globe-2 text-lg text-n-blue-700 dark:text-n-blue-300" />
          </div>
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <h3 class="text-sm font-medium text-n-slate-12">
                联网搜索
              </h3>
              <span
                v-tooltip="'当已关联知识库没有足够答案时，允许当前模型使用联网搜索补充回答。FAQ 命中时优先返回 FAQ 标准答案，不使用联网搜索。'"
                class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
              />
            </div>
            <p class="text-sm text-n-slate-11">
              当 FAQ 和文档没有足够答案时，允许模型使用公网信息补充回答。
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3 shrink-0">
          <span
            v-if="isSavingWebSearch"
            class="text-xs text-n-slate-10"
          >
            保存中...
          </span>
          <Switch
            :model-value="webSearchState.enabled"
            @update:model-value="handleToggleWebSearch"
          />
        </div>
      </div>

    </section>

    <!-- 知识库 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">知识库</h3>
          <span
            v-tooltip="'关联知识库，让助手获取专业知识来回答用户问题'"
            class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
          />
        </div>
      </div>

      <!-- 空状态引导 -->
      <div
        v-if="linkedKnowledgeBases.length === 0"
        class="relative overflow-hidden rounded-xl"
      >
        <!-- 类型介绍图片 -->
        <div class="grid grid-cols-2 gap-6 p-4 pb-0">
          <div class="flex flex-col items-center">
            <img
              :src="documentPopoverLight"
              class="w-full dark:hidden"
              alt="文档知识库"
            />
            <img
              :src="documentPopoverDark"
              class="w-full hidden dark:block"
              alt="文档知识库"
            />
            <p class="text-xs text-n-slate-10 mt-2 text-center">
              上传文档，助手自动学习
            </p>
          </div>
          <div class="flex flex-col items-center">
            <img
              :src="faqsPopoverLight"
              class="w-full dark:hidden"
              alt="FAQs 知识库"
            />
            <img
              :src="faqsPopoverDark"
              class="w-full hidden dark:block"
              alt="FAQs 知识库"
            />
            <p class="text-xs text-n-slate-10 mt-2 text-center">
              添加问答，精准回答
            </p>
          </div>
        </div>

        <!-- 背景假卡片 -->
        <div class="grid grid-cols-2 gap-4 p-4 opacity-50 pointer-events-none">
          <div class="space-y-4">
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-brand/10 flex items-center justify-center"
                >
                  <span class="i-lucide-file-text text-lg text-n-brand" />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    产品手册
                  </div>
                  <div class="text-xs text-n-slate-9">文档知识库</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                产品规格、使用说明、技术参数
              </div>
            </div>
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-amber-9/10 flex items-center justify-center"
                >
                  <span class="i-lucide-file-stack text-lg text-n-amber-9" />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    操作文档
                  </div>
                  <div class="text-xs text-n-slate-9">文档知识库</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                操作流程、配置指南、最佳实践
              </div>
            </div>
          </div>
          <div class="space-y-4">
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-emerald-9/10 flex items-center justify-center"
                >
                  <span
                    class="i-lucide-message-circle-question text-lg text-n-emerald-9"
                  />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    常见问题
                  </div>
                  <div class="text-xs text-n-slate-9">FAQs 知识库</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                产品使用、账户管理、订单查询
              </div>
            </div>
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-ruby-9/10 flex items-center justify-center"
                >
                  <span class="i-lucide-headset text-lg text-n-ruby-9" />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    售后服务
                  </div>
                  <div class="text-xs text-n-slate-9">FAQs 知识库</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                退换货政策、保修条款、维修流程
              </div>
            </div>
          </div>
        </div>

        <!-- 渐变蒙层 + 内容 -->
        <div
          class="absolute inset-0 flex flex-col items-center justify-end pb-10 bg-gradient-to-t from-[#F3F4F7] from-30% to-transparent dark:from-n-slate-7/50 dark:to-transparent"
        >
          <h3 class="text-xl font-medium text-n-slate-12 mb-2">
            关联知识库，让助手更智能
          </h3>
          <p class="text-sm text-n-slate-11 mb-4 max-w-md text-center">
            知识库包含文档和问答两种类型，关联后助手可以基于专业知识准确回答用户问题
          </p>
          <button
            class="px-4 py-2 bg-n-brand text-white text-sm rounded-lg hover:bg-n-brand-dark transition-colors"
            @click="openKnowledgeBaseSelector"
          >
            添加知识库
          </button>
        </div>
      </div>

      <!-- 已关联的知识库列表 -->
      <div v-else class="flex flex-col gap-2">
        <BulkSelectBar
          v-model="selectedKnowledgeBaseIds"
          :all-items="linkedKnowledgeBases"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          delete-label="批量移除"
          no-background
          @bulk-delete="handleBatchRemove"
        >
          <template #default-actions>
            <div class="flex justify-between items-center w-full">
              <Input v-model="searchQuery" size="sm" placeholder="搜索知识库" />
              <Button
                label="添加更多"
                sm
                slate
                @click="openKnowledgeBaseSelector"
              />
            </div>
          </template>
        </BulkSelectBar>

        <div
          v-for="kb in filteredLinkedKnowledgeBases"
          :key="kb.id"
          class="relative flex items-start justify-between w-full gap-2 p-4 rounded-xl outline-1 outline transition-colors"
          :class="[
            isSelected(kb.id)
              ? 'outline-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
              : 'outline-n-container bg-n-solid-2',
          ]"
          @mouseenter="hoveredKnowledgeBaseId = kb.id"
          @mouseleave="hoveredKnowledgeBaseId = null"
        >
          <!-- Checkbox 绝对定位在左上角，hover 或选中时显示 -->
          <div
            v-show="
              hoveredKnowledgeBaseId === kb.id ||
              selectedKnowledgeBaseIds.size > 0
            "
            class="absolute top-4 ltr:left-3 rtl:right-3"
          >
            <Checkbox
              :model-value="isSelected(kb.id)"
              @update:model-value="toggleSelection(kb.id)"
            />
          </div>

          <!-- 左侧内容 -->
          <div class="flex items-start gap-3 ltr:ml-6 rtl:mr-6 flex-1 min-w-0">
            <!-- Logo -->
            <div
              class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
              :class="kb.logo_url ? '' : 'bg-n-brand/10'"
            >
              <img
                v-if="kb.logo_url"
                :src="kb.logo_url"
                :alt="kb.name"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-base font-semibold text-n-brand">
                {{ kb.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
            </div>

            <!-- 信息 -->
            <div class="flex flex-col min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-12 font-medium">{{
                  kb.name
                }}</span>
                <span
                  v-if="kb.knowledge_base_type === 'faq'"
                  class="px-2 py-0.5 text-xs rounded-full font-medium"
                  :class="getTypeColorClass(kb)"
                >
                  {{ getTypeLabel(kb) }}
                </span>
              </div>
              <span class="text-sm text-n-slate-11 mt-2">
                {{ kb.description || '暂无描述' }}
              </span>
              <!-- 文档和FAQ数量 -->
              <div class="flex items-center gap-4 text-sm text-n-slate-11 mt-2">
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-file-text size-3" />
                  <span>{{ getDocumentsCount(kb) }} 文档</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-message-circle-question size-3" />
                  <span>{{ getFaqsCount(kb) }} FAQ</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧按钮 -->
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-trash"
              slate
              xs
              ghost
              :is-loading="removingId === kb.id"
              :disabled="removingId !== null"
              @click="handleRemoveKnowledgeBase(kb)"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 帮助中心 -->
    <section
      v-if="isHelpCenterEnabled"
      class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3"
    >
      <div class="flex items-center gap-2 mb-3">
        <h3 class="text-sm font-medium text-n-slate-12">帮助中心</h3>
        <span
          v-tooltip="'关联帮助中心，让助手获取帮助文档来回答用户问题'"
          class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
        />
      </div>

      <!-- 引导内容 -->
      <div
        v-if="linkedPortals.length === 0"
        class="relative overflow-hidden rounded-xl"
      >
        <div class="grid grid-cols-2 gap-4 p-4 opacity-50 pointer-events-none">
          <div class="space-y-4">
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-brand/10 flex items-center justify-center"
                >
                  <span class="i-lucide-book-open text-lg text-n-brand" />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    产品帮助中心
                  </div>
                  <div class="text-xs text-n-slate-9">12 篇文章</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                产品使用指南、功能介绍、操作教程
              </div>
            </div>
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-ruby-9/10 flex items-center justify-center"
                >
                  <span class="i-lucide-shield-check text-lg text-n-ruby-9" />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    安全与合规
                  </div>
                  <div class="text-xs text-n-slate-9">6 篇文章</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                数据安全、隐私政策、合规说明
              </div>
            </div>
          </div>
          <div class="space-y-4">
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-emerald-9/10 flex items-center justify-center"
                >
                  <span class="i-lucide-headphones text-lg text-n-emerald-9" />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    客服支持中心
                  </div>
                  <div class="text-xs text-n-slate-9">8 篇文章</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                常见问题、故障排除、联系方式
              </div>
            </div>
            <div
              class="p-4 rounded-xl bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
            >
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="w-10 h-10 rounded-lg bg-n-amber-9/10 flex items-center justify-center"
                >
                  <span
                    class="i-lucide-graduation-cap text-lg text-n-amber-9"
                  />
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    开发者文档
                  </div>
                  <div class="text-xs text-n-slate-9">25 篇文章</div>
                </div>
              </div>
              <div class="text-xs text-n-slate-10">
                API 文档、集成指南、SDK 使用
              </div>
            </div>
          </div>
        </div>

        <!-- 渐变蒙层 + 内容 -->
        <div
          class="absolute inset-0 flex flex-col items-center justify-end pb-10 bg-gradient-to-t from-[#F3F4F7] from-30% to-transparent dark:from-n-slate-7/50 dark:to-transparent"
        >
          <h3 class="text-xl font-medium text-n-slate-12 mb-2">
            关联帮助中心，扩展知识来源
          </h3>
          <p class="text-sm text-n-slate-11 mb-4 max-w-md text-center">
            帮助中心包含用户指南、常见问题、故障排除等内容，关联后助手可以引用文章解答用户问题
          </p>
          <button
            class="px-4 py-2 bg-n-brand text-white text-sm rounded-lg hover:bg-n-brand-dark transition-colors"
            @click="openHelpCenterSelector"
          >
            添加帮助中心
          </button>
        </div>
      </div>

      <!-- 已关联的帮助中心列表 -->
      <div v-else class="flex flex-col gap-2">
        <BulkSelectBar
          v-model="selectedPortalIds"
          :all-items="linkedPortals"
          :select-all-label="buildPortalSelectedCountLabel"
          :selected-count-label="selectedPortalCountLabel"
          delete-label="批量移除"
          no-background
          @bulk-delete="handleBatchRemovePortals"
        >
          <template #default-actions>
            <div class="flex justify-between items-center w-full">
              <Input
                v-model="portalSearchQuery"
                size="sm"
                placeholder="搜索帮助中心"
              />
              <Button
                label="添加更多"
                sm
                slate
                @click="openHelpCenterSelector"
              />
            </div>
          </template>
        </BulkSelectBar>

        <div
          v-for="portal in filteredLinkedPortals"
          :key="portal.id"
          class="relative flex items-start justify-between w-full gap-2 p-4 rounded-xl outline-1 outline transition-colors"
          :class="[
            isPortalSelected(portal.id)
              ? 'outline-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
              : 'outline-n-container bg-n-solid-2',
          ]"
          @mouseenter="hoveredPortalId = portal.id"
          @mouseleave="hoveredPortalId = null"
        >
          <!-- Checkbox 绝对定位在左上角，hover 或选中时显示 -->
          <div
            v-show="hoveredPortalId === portal.id || selectedPortalIds.size > 0"
            class="absolute top-4 ltr:left-3 rtl:right-3"
          >
            <Checkbox
              :model-value="isPortalSelected(portal.id)"
              @update:model-value="togglePortalSelection(portal.id)"
            />
          </div>

          <!-- 左侧内容 -->
          <div class="flex items-start gap-3 ltr:ml-6 rtl:mr-6 flex-1 min-w-0">
            <!-- Logo -->
            <div
              class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
              :class="portal.logo_url ? '' : 'bg-n-brand/10'"
            >
              <img
                v-if="portal.logo_url"
                :src="portal.logo_url"
                :alt="portal.name"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-base font-semibold text-n-brand">
                {{ portal.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
            </div>

            <!-- 信息 -->
            <div class="flex flex-col min-w-0">
              <span class="text-sm text-n-slate-12 font-medium">{{
                portal.name
              }}</span>
              <span class="text-sm text-n-slate-11 mt-2">
                {{ portal.header_text || '暂无描述' }}
              </span>
              <!-- 文章数量 -->
              <div class="flex items-center gap-4 text-sm text-n-slate-11 mt-2">
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-file-text size-3" />
                  <span>{{ portal.articles_count || 0 }} 文章</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧按钮 -->
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-trash"
              slate
              xs
              ghost
              :is-loading="removingPortalId === portal.id"
              :disabled="removingPortalId !== null"
              @click="handleRemovePortal(portal)"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 知识库选择弹窗 -->
    <KnowledgeBaseSelectorDialog
      ref="knowledgeBaseSelectorRef"
      :assistant-id="assistantId"
      @close="() => {}"
    />

    <!-- 帮助中心选择弹窗 -->
    <HelpCenterSelectorDialog
      v-if="isHelpCenterEnabled"
      ref="helpCenterSelectorRef"
      :assistant-id="assistantId"
      @close="() => {}"
    />
  </div>
</template>
